import type ReactModal from 'react-modal';

import type {Properties} from 'csstype';

import {inject, injectable} from 'inversify';
import {action, computed, makeObservable, observable, reaction} from 'mobx';

import type {Destructible, WithReactionDispose} from '../../../../utils/store';

import {ms} from '../../../../styles';
import {getUuid} from '../../../../utils/getUuid';
import {definedOnly} from '../../../../utils/object/definedOnly';
import {FlagManager, ReactionDisposeManager} from '../../../../utils/store';
import {
  MODAL_CLASS_NAMES,
  MODAL_OFFSET,
  MODAL_OVERLAY_Z_INDEX,
  MODAL_PADDING,
} from '../../constants';
import {ModalService} from '../ModalService/Modal.service';
import {ModalNodesManager} from './ModalNodesManager';
import {ModalResizeManager} from './ModalResizeManager';
import {ModalSelectorsManager} from './ModalSelectorsManager';

export namespace ModalConfig {}

@injectable()
export class ModalController implements Destructible, WithReactionDispose {
  config: ModalController.Config = {
    isClosable: true,
    isFullWidth: false,
    maxHeight: `calc(100% - ${ms(2 * MODAL_OFFSET)})`,
    maxWidth: `calc(100% - ${ms(2 * MODAL_OFFSET)})`,
    minHeight: 'initial',
    minWidth: ms(300 - 2 * MODAL_OFFSET),
    modalOffset: MODAL_OFFSET,
    modalPadding: MODAL_PADDING,
    overlayBlur: 3,
    overlayPosition: 'fixed',
    overlayZIndex: MODAL_OVERLAY_Z_INDEX,
  };
  disposer = new ReactionDisposeManager();
  readonly id = getUuid();
  isLoading = new FlagManager();
  isOpen = new FlagManager();
  isRefUsed = new FlagManager();
  nodes = new ModalNodesManager(this);
  resize = new ModalResizeManager(this);
  selectors = new ModalSelectorsManager(this);

  constructor(@inject(ModalService) public modalService: ModalService) {
    makeObservable(this, {
      config: observable,
      hide: action.bound,
      id: observable,
      init: action.bound,
      isAbleToClose: computed,
      show: action.bound,
    });
  }

  init(config: Partial<ModalController.InitialConfig>, isOpen: boolean) {
    const {
      modalOffset,
      modalPadding,
      overlayBlur,
      overlayPosition,
      renderCloseIcon,
    } = this.modalService.config;

    this.config = {
      ...this.config,
      modalOffset,
      modalPadding,
      overlayBlur: overlayBlur ?? 3,
      overlayPosition: overlayPosition ?? 'fixed',
      renderCloseIcon,
      ...definedOnly(config),
    };
    this.setupReactions();

    return isOpen ? this.show() : this.hide();
  }

  destruct() {
    this.resize.destruct();
    this.disposer.dispose();
    this.modalService.unregister(this);
  }

  get isAbleToClose() {
    return this.isLoading.isFalse && this.config.isClosable;
  }

  handleRefOpenProp(isOpen: boolean) {
    if (this.isRefUsed.isFalse && this.isOpen.value !== isOpen) {
      return isOpen ? this.show() : this.hide();
    }
  }

  hide() {
    this.modalService.unregister(this);
    this.isOpen.setFalse();
  }

  show() {
    this.modalService.register(this);
    this.isOpen.setTrue();
  }

  private onOpenChangeReaction(isOpen: boolean) {
    const body = document.body;

    if (isOpen) {
      body.classList.add(MODAL_CLASS_NAMES.noScroll);

      return;
    }

    if (!this.modalService.isAnyModalOpen) {
      body.classList.remove(MODAL_CLASS_NAMES.noScroll);

      return;
    }
  }

  private setupReactions() {
    this.disposer.watch(
      'onOpenChange',
      reaction(
        () => this.isOpen.value,
        value => this.onOpenChangeReaction(value),
      ),
    );
  }
}

export namespace ModalController {
  export type Config = {
    isClosable: boolean;
    isFullWidth: boolean;
    maxHeight: Properties['maxHeight'];
    maxWidth: Properties['maxWidth'];
    minHeight: Properties['minHeight'];
    minWidth: Properties['minWidth'];
    modalOffset: number;
    modalPadding: number;
    overlayBlur: boolean | number;
    overlayPosition: 'absolute' | 'fixed';
    overlayZIndex: number;
    renderCloseIcon?: () => React.ReactNode;
  };

  export type InitialConfig = Partial<Config>;

  export type ReactModalClassName = ReactModal.Classes | string;
}
